import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../../contexts/LanguageContext";
import { useAuth } from "../../../contexts/AuthContext";
import {
  getConversations,
  getConversationDetails,
  sendConversationMessage,
} from "../../../api/conversations";
import {
  createCustomOffer,
  acceptCustomOffer,
  rejectCustomOffer,
} from "../../../api/customOffers";
import CustomOfferModal from "../../../components/chat/CustomOfferModal";
import logger from "../../../utils/logger";
import {
  MessageSquare,
  Search,
  Send,
  Paperclip,
  User,
  Clock,
  Download,
  Loader2,
  AlertTriangle,
  ArrowLeft,
  Store,
  RefreshCw,
  Sparkles,
  CheckCircle,
  XCircle,
  Calendar,
  DollarSign,
  ShieldCheck,
  Check,
  X,
} from "lucide-react";
import "./ConversationsTab.css";

const avatarFallback = "https://ui-avatars.com/api/?background=2563eb&color=ffffff&name=User";

export default function ConversationsTab() {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAr = lang === "ar";

  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [loadingList, setLoadingList] = useState(true);
  const [loadingChat, setLoadingChat] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const [messageText, setMessageText] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [mobileShowChat, setMobileShowChat] = useState(false);

  // Custom Offer Modal State
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);
  const [processingOfferId, setProcessingOfferId] = useState(null);

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadConversationsList = useCallback(async (autoSelectFirst = true) => {
    try {
      setLoadingList(true);
      setError("");
      const data = await getConversations();
      setConversations(data || []);

      if (autoSelectFirst && data && data.length > 0 && !activeConvId) {
        setActiveConvId(data[0].id);
      }
    } catch (err) {
      logger.error("Error loading conversations", err);
      setError(isAr ? "تعذر تحميل قائمة المحادثات." : "Failed to load conversations list.");
    } finally {
      setLoadingList(false);
    }
  }, [activeConvId, isAr]);

  const loadChatDetails = useCallback(async (convId) => {
    if (!convId) return;
    try {
      setLoadingChat(true);
      setError("");
      const data = await getConversationDetails(convId);
      setActiveConv(data);
      setMessages(Array.isArray(data?.messages) ? data.messages : []);
      setTimeout(scrollToBottom, 100);
    } catch (err) {
      logger.error("Error loading conversation details", err);
      setError(isAr ? "تعذر تحميل الرسائل." : "Failed to load messages.");
    } finally {
      setLoadingChat(false);
    }
  }, [isAr]);

  useEffect(() => {
    loadConversationsList(true);
  }, []);

  useEffect(() => {
    if (activeConvId) {
      loadChatDetails(activeConvId);
    }
  }, [activeConvId, loadChatDetails]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSelectConv = (id) => {
    setActiveConvId(id);
    setMobileShowChat(true);
  };

  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    if (!activeConvId || sending) return;
    const text = messageText.trim();
    if (!text && !attachment) return;

    try {
      setSending(true);
      setError("");

      let payload = { message: text };
      if (attachment) {
        const fd = new FormData();
        fd.append("message", text);
        fd.append("attachment", attachment);
        payload = fd;
      }

      const newMsg = await sendConversationMessage(activeConvId, payload);
      if (newMsg) {
        setMessages((prev) => [...prev, newMsg]);
        setMessageText("");
        setAttachment(null);
        scrollToBottom();
      }

      loadConversationsList(false);
    } catch (err) {
      logger.error("Error sending message", err);
      setError(isAr ? "تعذر إرسال الرسالة." : "Failed to send message.");
    } finally {
      setSending(false);
    }
  };

  // Handle Custom Offer Submission
  const handleCreateOfferSubmit = async (payload) => {
    try {
      await createCustomOffer(payload);
      setIsOfferModalOpen(false);
      await loadChatDetails(activeConvId);
      loadConversationsList(false);
    } catch (err) {
      logger.error("Error creating custom offer", err);
      throw err;
    }
  };

  // Handle Accept Custom Offer
  const handleAcceptOffer = async (offerId) => {
    if (!offerId || processingOfferId) return;
    try {
      setProcessingOfferId(offerId);
      setError("");
      const result = await acceptCustomOffer(offerId);
      await loadChatDetails(activeConvId);
      loadConversationsList(false);

      if (result?.created_order_id || result?.id) {
        navigate("/my-service-orders");
      }
    } catch (err) {
      logger.error("Error accepting offer", err);
      setError(err?.response?.data?.message || (isAr ? "تعذر قبول العرض." : "Failed to accept offer."));
    } finally {
      setProcessingOfferId(null);
    }
  };

  // Handle Reject Custom Offer
  const handleRejectOffer = async (offerId) => {
    if (!offerId || processingOfferId) return;
    try {
      setProcessingOfferId(offerId);
      setError("");
      await rejectCustomOffer(offerId);
      await loadChatDetails(activeConvId);
      loadConversationsList(false);
    } catch (err) {
      logger.error("Error rejecting offer", err);
      setError(err?.response?.data?.message || (isAr ? "تعذر رفض العرض." : "Failed to reject offer."));
    } finally {
      setProcessingOfferId(null);
    }
  };

  const getOtherParticipant = (conv) => {
    if (!conv || !user) return null;
    const currentUserId = Number(user.id);
    if (Number(conv.client_id) === currentUserId) {
      return conv.freelancer || { name: isAr ? "المستقل" : "Freelancer" };
    }
    return conv.client || { name: isAr ? "العميل" : "Client" };
  };

  const filteredConversations = conversations.filter((conv) => {
    const participant = getOtherParticipant(conv);
    const pName = participant?.name || "";
    const sTitle = conv.service?.title || conv.service?.title_ar || "";
    const query = searchQuery.toLowerCase();
    return pName.toLowerCase().includes(query) || sTitle.toLowerCase().includes(query);
  });

  const activeParticipant = getOtherParticipant(activeConv);

  return (
    <div className="convs-tab-wrapper" dir={isAr ? "rtl" : "ltr"}>
      <div className={`convs-layout ${mobileShowChat ? "show-mobile-chat" : ""}`}>
        {/* Left / Sidebar Pane */}
        <aside className="convs-sidebar">
          <div className="convs-sidebar-header">
            <div className="convs-sidebar-title-group">
              <h3>{isAr ? "المحادثات والرسائل" : "Messages & Conversations"}</h3>
              <button
                type="button"
                className="convs-refresh-btn"
                onClick={() => loadConversationsList(false)}
                title={isAr ? "تحديث القائمة" : "Refresh list"}
              >
                <RefreshCw size={15} />
              </button>
            </div>

            <div className="convs-search-box">
              <Search size={16} className="convs-search-icon" />
              <input
                type="text"
                placeholder={isAr ? "ابحث باسم المستخدم أو الخدمة..." : "Search user or service..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="convs-sidebar-list">
            {loadingList ? (
              <div className="convs-loading-state">
                <Loader2 size={24} className="convs-spinner" />
                <span>{isAr ? "جارِ التحميل..." : "Loading..."}</span>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="convs-empty-sidebar">
                <MessageSquare size={32} strokeWidth={1.5} />
                <p>{isAr ? "لا توجد محادثات مطابقة" : "No matching conversations"}</p>
              </div>
            ) : (
              filteredConversations.map((conv) => {
                const participant = getOtherParticipant(conv);
                const isSelected = conv.id === activeConvId;
                const lastMsg = conv.last_message || conv.lastMessage;
                const avatarSrc = participant?.avatar_url || avatarFallback;
                const serviceTitle = conv.service?.title || conv.service?.title_ar;

                return (
                  <div
                    key={conv.id}
                    className={`conv-item-card ${isSelected ? "active" : ""}`}
                    onClick={() => handleSelectConv(conv.id)}
                  >
                    <div className="conv-avatar-wrap">
                      <img
                        src={avatarSrc}
                        alt={participant?.name || "User"}
                        onError={(e) => {
                          e.currentTarget.src = avatarFallback;
                        }}
                      />
                      <span className="conv-status-dot" />
                    </div>

                    <div className="conv-item-info">
                      <div className="conv-item-top">
                        <h4 className="conv-item-name">
                          {participant?.name || (isAr ? "مستخدم" : "User")}
                        </h4>
                        {lastMsg?.created_at && (
                          <span className="conv-item-time">
                            {new Date(lastMsg.created_at).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        )}
                      </div>

                      {serviceTitle && (
                        <div className="conv-service-tag">
                          <Store size={12} />
                          <span>{serviceTitle}</span>
                        </div>
                      )}

                      <p className="conv-item-preview">
                        {lastMsg?.message || (isAr ? "لا توجد رسائل بعد" : "No messages yet")}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </aside>

        {/* Right / Main Chat Workspace */}
        <main className="convs-chat-main">
          {activeConvId && activeConv ? (
            <>
              {/* Chat Header */}
              <div className="convs-chat-header">
                <button
                  type="button"
                  className="convs-mobile-back"
                  onClick={() => setMobileShowChat(false)}
                >
                  <ArrowLeft size={20} />
                </button>

                <div className="convs-user-meta">
                  <img
                    src={activeParticipant?.avatar_url || avatarFallback}
                    alt={activeParticipant?.name}
                    className="convs-chat-avatar"
                    onError={(e) => {
                      e.currentTarget.src = avatarFallback;
                    }}
                  />
                  <div>
                    <h3 className="convs-chat-username">
                      {activeParticipant?.name || (isAr ? "مستخدم" : "User")}
                    </h3>
                    {activeConv.service && (
                      <span className="convs-chat-service-info">
                        {isAr ? "الخدمة المرتبطة:" : "Service:"}{" "}
                        <strong>{activeConv.service.title || activeConv.service.title_ar}</strong>
                      </span>
                    )}
                  </div>
                </div>

                {/* Custom Offer Trigger Button */}
                <button
                  type="button"
                  className="convs-custom-offer-btn"
                  onClick={() => setIsOfferModalOpen(true)}
                  title={isAr ? "تقديم عرض مخصص لهذا العميل" : "Create custom offer"}
                >
                  <Sparkles size={16} />
                  <span>{isAr ? "عرض مخصص" : "Custom Offer"}</span>
                </button>
              </div>

              {/* Chat Thread */}
              <div className="convs-messages-thread">
                {loadingChat ? (
                  <div className="convs-loading-state">
                    <Loader2 size={32} className="convs-spinner" />
                    <span>{isAr ? "جارِ تحميل المحادثة..." : "Loading conversation..."}</span>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="convs-thread-empty">
                    <MessageSquare size={44} strokeWidth={1.5} />
                    <p>{isAr ? "ابدأ المحادثة الآن عن طريق كتابة أول رسالة بالأسفل" : "Start the conversation by sending a message below."}</p>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isOwn = Number(msg.sender_id) === Number(user?.id);
                    const senderName = msg.sender?.name || (isOwn ? (isAr ? "أنت" : "You") : activeParticipant?.name);
                    const senderAvatar = msg.sender?.avatar_url || (isOwn ? user?.avatar_url : activeParticipant?.avatar_url) || avatarFallback;
                    const customOffer = msg.customOffer || msg.custom_offer;

                    return (
                      <div key={msg.id} className={`conv-msg-row ${isOwn ? "msg-own" : "msg-other"}`}>
                        {!isOwn && (
                          <img
                            src={senderAvatar}
                            alt={senderName}
                            className="msg-sender-avatar"
                            onError={(e) => {
                              e.currentTarget.src = avatarFallback;
                            }}
                          />
                        )}

                        <div className="msg-bubble-container">
                          <div className="msg-meta-row">
                            <span className="msg-sender-name">{senderName}</span>
                            {msg.created_at && (
                              <span className="msg-timestamp">
                                {new Date(msg.created_at).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            )}
                          </div>

                          <div className={`msg-bubble ${customOffer ? "msg-bubble-offer" : ""}`}>
                            {/* Render Custom Offer Card if attached */}
                            {customOffer ? (
                              <div className="msg-offer-card">
                                <div className="msg-offer-header">
                                  <div className="msg-offer-badge">
                                    <Sparkles size={16} />
                                    <span>{isAr ? "عرض مخصص (Custom Offer)" : "Custom Offer"}</span>
                                  </div>
                                  <span className={`msg-offer-status status-${customOffer.status}`}>
                                    {customOffer.status === "pending" && (isAr ? "قيد الانتظار" : "Pending")}
                                    {customOffer.status === "accepted" && (isAr ? "تم القبول" : "Accepted")}
                                    {customOffer.status === "rejected" && (isAr ? "مرفوض" : "Rejected")}
                                    {customOffer.status === "expired" && (isAr ? "منتهي الصلاحية" : "Expired")}
                                  </span>
                                </div>

                                <h4 className="msg-offer-title">{customOffer.title}</h4>

                                {customOffer.description && (
                                  <p className="msg-offer-desc">{customOffer.description}</p>
                                )}

                                <div className="msg-offer-details-grid">
                                  <div className="msg-offer-stat">
                                    <DollarSign size={15} />
                                    <span>{isAr ? "السعر:" : "Price:"}</span>
                                    <strong>${Number(customOffer.price).toFixed(2)} USD</strong>
                                  </div>
                                  <div className="msg-offer-stat">
                                    <Calendar size={15} />
                                    <span>{isAr ? "التسليم:" : "Delivery:"}</span>
                                    <strong>{customOffer.delivery_days} {isAr ? "أيام" : "days"}</strong>
                                  </div>
                                  <div className="msg-offer-stat">
                                    <RefreshCw size={15} />
                                    <span>{isAr ? "التعديلات:" : "Revisions:"}</span>
                                    <strong>{customOffer.revisions ?? 0}</strong>
                                  </div>
                                </div>

                                {customOffer.expires_at && (
                                  <div className="msg-offer-expiry">
                                    <Clock size={13} />
                                    <span>
                                      {isAr ? "ينتهي في:" : "Expires on:"}{" "}
                                      {new Date(customOffer.expires_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                )}

                                {/* Offer Actions for Client */}
                                {customOffer.status === "pending" && !isOwn && (
                                  <div className="msg-offer-actions">
                                    <button
                                      type="button"
                                      className="msg-offer-accept-btn"
                                      onClick={() => handleAcceptOffer(customOffer.id)}
                                      disabled={processingOfferId === customOffer.id}
                                    >
                                      {processingOfferId === customOffer.id ? (
                                        <Loader2 size={15} className="convs-spinner" />
                                      ) : (
                                        <CheckCircle size={15} />
                                      )}
                                      <span>{isAr ? "قبول والدفع" : "Accept & Pay"}</span>
                                    </button>

                                    <button
                                      type="button"
                                      className="msg-offer-reject-btn"
                                      onClick={() => handleRejectOffer(customOffer.id)}
                                      disabled={processingOfferId === customOffer.id}
                                    >
                                      <XCircle size={15} />
                                      <span>{isAr ? "رفض" : "Reject"}</span>
                                    </button>
                                  </div>
                                )}

                                {customOffer.status === "accepted" && (
                                  <div className="msg-offer-notice notice-accepted">
                                    <CheckCircle size={15} />
                                    <span>{isAr ? "تم قبول هذا العرض وإنشاء الطلب بنجاح." : "This custom offer was accepted and the order created."}</span>
                                  </div>
                                )}

                                {customOffer.status === "rejected" && (
                                  <div className="msg-offer-notice notice-rejected">
                                    <XCircle size={15} />
                                    <span>{isAr ? "تم رفض هذا العرض المخصص." : "This custom offer was rejected."}</span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              msg.message
                            )}

                            {msg.attachment_url && (
                              <a
                                href={msg.attachment_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="msg-attachment-btn"
                              >
                                <Download size={14} />
                                <span>{isAr ? "تحميل المرفق" : "Download Attachment"}</span>
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Error Notice */}
              {error && (
                <div className="convs-chat-error">
                  <AlertTriangle size={16} />
                  <span>{error}</span>
                </div>
              )}

              {/* Compose Bar */}
              <form className="convs-compose-bar" onSubmit={handleSendMessage}>
                <div className="convs-input-wrapper">
                  <textarea
                    rows={1}
                    placeholder={isAr ? "اكتب رسالتك هنا..." : "Type your message..."}
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />

                  <div className="convs-compose-actions">
                    <label className="convs-attach-btn" title={isAr ? "إرفاق ملف" : "Attach file"}>
                      <Paperclip size={18} />
                      <input
                        type="file"
                        onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                        hidden
                      />
                    </label>

                    {attachment && (
                      <span className="convs-file-name" title={attachment.name}>
                        {attachment.name}
                      </span>
                    )}

                    <button
                      type="button"
                      className="convs-compose-offer-btn"
                      onClick={() => setIsOfferModalOpen(true)}
                      title={isAr ? "تقديم عرض مخصص" : "Create custom offer"}
                    >
                      <Sparkles size={15} />
                      <span>{isAr ? "عرض مخصص" : "Custom Offer"}</span>
                    </button>

                    <button
                      type="submit"
                      className="convs-send-btn"
                      disabled={sending || (!messageText.trim() && !attachment)}
                    >
                      {sending ? (
                        <Loader2 size={16} className="convs-spinner" />
                      ) : (
                        <Send size={16} />
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </>
          ) : (
            <div className="convs-no-selection">
              <MessageSquare size={54} strokeWidth={1.2} />
              <h3>{isAr ? "اختر محادثة للبدء" : "Select a Conversation"}</h3>
              <p>
                {isAr
                  ? "اختر أحد المحادثات في القائمة الجانبية لبدء التواصل والتراسل المباشر."
                  : "Choose a conversation from the sidebar to view messages and reply."}
              </p>
            </div>
          )}
        </main>
      </div>

      {/* Render Custom Offer Creation Modal */}
      {activeConv && (
        <CustomOfferModal
          isOpen={isOfferModalOpen}
          onClose={() => setIsOfferModalOpen(false)}
          onSubmit={handleCreateOfferSubmit}
          conversation={activeConv}
        />
      )}
    </div>
  );
}
